// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app"
import { getAnalytics } from "firebase/analytics"
import { getAuth } from "firebase/auth"
import { getStorage } from "firebase/storage"
import { getFirestore, terminate, initializeFirestore } from "firebase/firestore"

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyC-yQ1yb1HDNwqXYFlVOtMorUnGw6MFvbs",
  authDomain: "lgstakip-78404.firebaseapp.com",
  projectId: "lgstakip-78404",
  storageBucket: "lgstakip-78404.firebasestorage.app",
  messagingSenderId: "212430563569",
  appId: "1:212430563569:web:0d744c8cf49cbf9d5be150",
  measurementId: "G-7J22YK1NH6"
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// Initialize Firebase services
export const auth = getAuth(app)
export const storage = getStorage(app)

// Initialize Firestore with better error handling
let db: any
try {
  db = getFirestore(app)
} catch (error) {
  console.warn('Initial Firestore initialization failed, retrying...', error)
  db = initializeFirestore(app, {})
}

export { db }

// Connection state management
let isConnected = false
let connectionRetries = 0
const maxRetries = 3

// Test Firebase connection with retry logic
export const testFirebaseConnection = async () => {
  try {
    console.log('🔍 Testing Firebase connection...')
    console.log('Auth state:', auth.currentUser ? 'User logged in' : 'No user')
    console.log('Database instance:', db ? 'Available' : 'Not available')
    
    if (db) {
      console.log('✅ Firestore is properly initialized')
      isConnected = true
      connectionRetries = 0
      
      // Development mode warning
      if (window.location.hostname === 'localhost') {
        console.log('🚨 DEVELOPMENT MODE: Firebase güvenlik kurallarını güncelleyin!')
        console.log('📋 Firebase Console: https://console.firebase.google.com/project/lgstakip-78404/firestore/rules')
        console.log('📝 Gerekli kural: allow read, write: if request.auth != null;')
      }
      
      return true
    } else {
      console.log('❌ Firestore is not properly initialized')
      isConnected = false
      return false
    }
  } catch (error) {
    console.error('❌ Firebase connection test failed:', error)
    isConnected = false
    return false
  }
}

// Check connection status
export const isFirebaseConnected = () => isConnected

// Retry connection with exponential backoff
export const retryConnection = async (): Promise<boolean> => {
  if (connectionRetries >= maxRetries) {
    console.error('❌ Max connection retries reached')
    return false
  }
  
  connectionRetries++
  const delay = Math.pow(2, connectionRetries) * 1000 // Exponential backoff
  
  console.log(`🔄 Retrying connection (attempt ${connectionRetries}/${maxRetries}) in ${delay}ms...`)
  
  await new Promise(resolve => setTimeout(resolve, delay))
  return await testFirebaseConnection()
}

// Enhanced error handler for Firestore operations
export const handleFirestoreError = (error: any, operation: string) => {
  console.error(`Firestore ${operation} error:`, error)
  
  // Check for specific Firebase errors
  if (error?.code === 'unavailable' || error?.message?.includes('INTERNAL ASSERTION FAILED')) {
    console.warn('🔄 Firestore connection issue detected, attempting retry...')
    return retryConnection()
  }
  
  if (error?.code === 'permission-denied') {
    console.error('🚫 Permission denied - check Firebase security rules')
  }
  
  if (error?.code === 'unauthenticated') {
    console.error('🔐 User not authenticated')
  }
  
  return Promise.reject(error)
}

// Force clear Firestore cache and restart connection
export const clearFirestoreCache = async () => {
  try {
    if (typeof window !== 'undefined' && 'indexedDB' in window) {
      // Clear Firestore offline cache
      const databases = await indexedDB.databases()
      const firestoreDBs = databases.filter(db => db.name?.includes('firestore'))
      
      for (const dbInfo of firestoreDBs) {
        if (dbInfo.name) {
          const deleteReq = indexedDB.deleteDatabase(dbInfo.name)
          await new Promise((resolve, reject) => {
            deleteReq.onsuccess = () => resolve(true)
            deleteReq.onerror = () => reject(deleteReq.error)
          })
          console.log(`🗑️ Cleared Firestore cache: ${dbInfo.name}`)
        }
      }
    }
  } catch (error) {
    console.warn('Failed to clear Firestore cache:', error)
  }
}

// Force restart Firestore connection
export const forceRestartFirestore = async () => {
  try {
    console.log('🔄 Force restarting Firestore connection with complete reset...')
    
    // Step 1: Clear all caches and local storage
    await clearFirestoreCache()
    
    // Clear any Firestore-related localStorage
    if (typeof window !== 'undefined') {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.includes('firebase') || key.includes('firestore')) {
          localStorage.removeItem(key)
        }
      })
    }
    
    // Step 2: Terminate existing connection with multiple attempts
    let terminateAttempts = 0
    const maxTerminateAttempts = 3
    
    while (terminateAttempts < maxTerminateAttempts) {
      try {
        await terminate(db)
        console.log('✅ Firestore connection terminated successfully')
        break
      } catch (terminateError) {
        terminateAttempts++
        console.warn(`Terminate attempt ${terminateAttempts} failed:`, terminateError)
        if (terminateAttempts < maxTerminateAttempts) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
    }
    
    // Step 3: Wait longer for complete cleanup
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // Step 4: Reinitialize Firestore with fresh settings
    try {
      console.log('🔄 Creating new Firestore instance...')
      const newDb = initializeFirestore(app, {
        ignoreUndefinedProperties: true,
        experimentalForceLongPolling: false
      })
      
      // Replace the global db reference completely
      Object.setPrototypeOf(db, Object.getPrototypeOf(newDb))
      Object.assign(db, newDb)
      
      console.log('✅ New Firestore instance created with fresh settings')
    } catch (initError) {
      console.error('Failed to create new Firestore instance:', initError)
      // Fallback to getFirestore with retry
      let fallbackAttempts = 0
      while (fallbackAttempts < 3) {
        try {
          const fallbackDb = getFirestore(app)
          Object.assign(db, fallbackDb)
          console.log('✅ Fallback Firestore instance created')
          break
        } catch (fallbackError) {
          fallbackAttempts++
          if (fallbackAttempts >= 3) throw fallbackError
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }
    }
    
    // Step 5: Test new connection with timeout
    const connectionTest = Promise.race([
      testFirebaseConnection(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Connection test timeout')), 10000)
      )
    ])
    
    await connectionTest
    console.log('✅ Firestore connection restarted and tested successfully')
    
  } catch (error) {
    console.error('❌ Failed to restart Firestore completely:', error)
    // Don't throw - return gracefully to allow fallback mechanisms
    console.warn('⚠️ Continuing with potentially unstable Firestore connection')
  }
}

// Test connection on load with cache clearing
if (typeof window !== 'undefined') {
  setTimeout(async () => {
    // Clear cache first if there are connection issues
    if (!isConnected) {
      await clearFirestoreCache()
    }
    await testFirebaseConnection()
  }, 1000)
}

// Initialize Analytics (optional - only in production)
export let analytics: any = null
try {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    analytics = getAnalytics(app)
  }
} catch (error) {
  console.warn('Analytics initialization failed:', error)
}

export default app